import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { EASE_OUT } from '@/lib/motion';
import { palette } from '@/styles/figma-tokens';
import StepCard from '../shared/StepCard';
import InfoTooltip from '../shared/InfoTooltip';
import ReminderBanner from '../ReminderBanner';
import Switch from '@/components/common/Switch';
import SearchableSelect from '@/components/common/SearchableSelect';
import { FEATURE, planHasFeature } from '@/components/settings/tiers/tierCatalog';
import {
  REMINDER_MESSAGES,
  REMINDER_STORAGE_KEYS,
} from '../wizardTypes';
import {
  useGamificationConfig,
  useMilestones,
  useEnableGamification,
  useDisableGamification,
  useCreateMilestone,
  useUpdateMilestone,
  useDeleteMilestone,
} from '../../../../services/gamification';
import { useSubscription } from '../../../../services/billing';
import type { MilestoneType, Milestone } from '../../../../types/gamification';

// ─── Props ───────────────────────────────────────────────────────────────────

export interface GamificationStepProps {
  courseId: string;
  disabled?: boolean;
  onSaved: () => void;
  /** Stagger delay forwarded to StepCard (seconds). */
  entranceDelay?: number;
  /** True when the wizard is editing an existing course; the step starts collapsed. */
  editMode?: boolean;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const MILESTONE_TYPE_LABELS: Record<MilestoneType, string> = {
  highest_score: 'Highest Score',
  first_to_finish_and_pass: 'First to Finish & Pass',
  pass_the_course: 'Pass the Course',
  first_10_to_pass: 'First 10 to Pass',
  complete_all_sections: 'Complete All Sections',
  perfect_score: 'Perfect Score',
  fastest_completion: 'Fastest Completion',
  consistent_learner: 'Consistent Learner',
  top_3_scorers: 'Top 3 Scorers',
  hundred_percent_attendance: '100% Attendance',
};

const ALL_MILESTONE_TYPES = Object.keys(MILESTONE_TYPE_LABELS) as MilestoneType[];

// ─── Styles ───────────────────────────────────────────────────────────────────

const PURPLE = 'rgb(var(--color-primary))';
const PURPLE_DARK = 'rgb(var(--color-primary-600))';
const MUTED = 'rgb(var(--color-text-secondary))';
const BORDER_COLOR = 'rgb(var(--color-border-primary) / 0.45)';
const FONT = 'Poppins, sans-serif';

// ─── Sub-components ───────────────────────────────────────────────────────────

/** Trash / delete icon */
function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M19 6l-1 14H6L5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/** Plus icon */
function PlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

// ─── Draft Row ────────────────────────────────────────────────────────────────

interface DraftRow {
  localId: string;
  milestone_type: MilestoneType;
  points: string; // string to allow free typing before commit
  saving: boolean;
}

// ─── Milestone Row (persisted) ────────────────────────────────────────────────

function PersistedMilestoneRow({
  milestone,
  courseId,
  onUpdate,
  onDelete,
}: {
  milestone: Milestone;
  courseId: string;
  onUpdate: (milestoneId: string, points: number) => void;
  onDelete: (milestoneId: string) => void;
}) {
  const [localPoints, setLocalPoints] = useState(String(milestone.points));
  const [isDirty, setIsDirty] = useState(false);

  const updateMilestone = useUpdateMilestone();
  const deleteMilestone = useDeleteMilestone();

  const handlePointsBlur = () => {
    if (!isDirty) return;
    const parsed = parseInt(localPoints, 10);
    if (isNaN(parsed) || parsed < 1) {
      setLocalPoints(String(milestone.points));
      setIsDirty(false);
      return;
    }
    onUpdate(milestone.id, parsed);
    updateMilestone.mutate(
      { courseId, milestoneId: milestone.id, data: { points: parsed } },
      { onSettled: () => setIsDirty(false) }
    );
  };

  const handleDelete = () => {
    deleteMilestone.mutate(
      { courseId, milestoneId: milestone.id },
      { onSuccess: () => onDelete(milestone.id) }
    );
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 120px 36px',
        gap: '10px',
        alignItems: 'center',
      }}
    >
      {/* Milestone type — display only (type is immutable after creation) */}
      <div
        style={{
          padding: '8px 12px',
          border: `1px solid ${BORDER_COLOR}`,
          borderRadius: '8px',
          fontFamily: FONT,
          fontSize: '14px',
          color: 'rgb(var(--color-text-primary))',
          background: 'rgba(189,188,201,0.05)',
          userSelect: 'none',
          display: 'flex',
          alignItems: 'center',
          minHeight: '40px',
        }}
        title="Milestone type cannot be changed after creation. Delete and re-add to use a different type."
      >
        {MILESTONE_TYPE_LABELS[milestone.milestone_type]}
      </div>

      {/* Points input */}
      <input
        type="number"
        min={1}
        value={localPoints}
        onChange={(e) => {
          setLocalPoints(e.target.value);
          setIsDirty(true);
        }}
        onBlur={handlePointsBlur}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
        }}
        aria-label="Score points"
        style={{
          padding: '8px 12px',
          border: `1px solid ${isDirty ? PURPLE : BORDER_COLOR}`,
          borderRadius: '8px',
          fontFamily: FONT,
          fontSize: '14px',
          color: 'rgb(var(--color-text-primary))',
          background: 'rgb(var(--color-surface-primary))',
          outline: 'none',
          width: '100%',
          boxSizing: 'border-box',
          transition: 'border-color 0.15s',
          minHeight: '40px',
        }}
        onFocus={(e) => {
          (e.currentTarget as HTMLInputElement).style.borderColor = PURPLE;
        }}
      />

      {/* Delete button */}
      <button
        type="button"
        onClick={handleDelete}
        disabled={deleteMilestone.isPending}
        aria-label={`Delete ${MILESTONE_TYPE_LABELS[milestone.milestone_type]} milestone`}
        title="Delete milestone"
        className="active:scale-[0.92] disabled:active:scale-100"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          border: `1px solid rgba(189,188,201,0.5)`,
          background: 'transparent',
          color: MUTED,
          cursor: deleteMilestone.isPending ? 'not-allowed' : 'pointer',
          opacity: deleteMilestone.isPending ? 0.5 : 1,
          transition: 'color 0.15s, border-color 0.15s, background 0.15s, transform 0.15s',
          flexShrink: 0,
        }}
        onMouseEnter={(e) => {
          const btn = e.currentTarget as HTMLButtonElement;
          btn.style.color = palette.error['500'];
          btn.style.borderColor = 'rgba(224,82,82,0.4)';
          btn.style.background = 'rgba(224,82,82,0.06)';
        }}
        onMouseLeave={(e) => {
          const btn = e.currentTarget as HTMLButtonElement;
          btn.style.color = MUTED;
          btn.style.borderColor = 'rgba(189,188,201,0.5)';
          btn.style.background = 'transparent';
        }}
      >
        <TrashIcon />
      </button>
    </div>
  );
}

// ─── Draft Milestone Row ──────────────────────────────────────────────────────

function DraftMilestoneRow({
  row,
  usedTypes,
  onChange,
  onCommit,
  onDiscard,
}: {
  row: DraftRow;
  usedTypes: Set<MilestoneType>;
  onChange: (localId: string, patch: Partial<Pick<DraftRow, 'milestone_type' | 'points'>>) => void;
  onCommit: (localId: string) => void;
  onDiscard: (localId: string) => void;
}) {
  // Available types = all minus those used by OTHER rows; this row's own type
  // stays selectable so changing one row never knocks another row's value out.
  const availableTypes = ALL_MILESTONE_TYPES.filter(
    (t) => t === row.milestone_type || !usedTypes.has(t)
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={{ duration: 0.18, ease: EASE_OUT }}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 120px 36px',
        gap: '10px',
        alignItems: 'center',
      }}
    >
      {/* Milestone type dropdown — shared app SearchableSelect */}
      <SearchableSelect
        value={row.milestone_type}
        onChange={(val) => {
          if (val) onChange(row.localId, { milestone_type: val as MilestoneType });
        }}
        options={availableTypes.map((t) => ({ value: t, label: MILESTONE_TYPE_LABELS[t] }))}
        allLabel="Choose a milestone…"
        disabled={row.saving}
      />

      {/* Points input */}
      <input
        type="number"
        min={1}
        value={row.points}
        onChange={(e) => onChange(row.localId, { points: e.target.value })}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onCommit(row.localId);
          if (e.key === 'Escape') onDiscard(row.localId);
        }}
        onBlur={() => onCommit(row.localId)}
        disabled={row.saving}
        placeholder="e.g. 100"
        aria-label="Score points"
        style={{
          padding: '8px 12px',
          border: `1px solid ${BORDER_COLOR}`,
          borderRadius: '8px',
          fontFamily: FONT,
          fontSize: '14px',
          color: 'rgb(var(--color-text-primary))',
          background: 'rgb(var(--color-surface-primary))',
          outline: 'none',
          width: '100%',
          boxSizing: 'border-box',
          minHeight: '40px',
          opacity: row.saving ? 0.6 : 1,
        }}
      />

      {/* Discard button */}
      <button
        type="button"
        onClick={() => onDiscard(row.localId)}
        disabled={row.saving}
        aria-label="Discard new milestone"
        title="Discard"
        className="active:scale-[0.92] disabled:active:scale-100"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          border: `1px solid rgba(189,188,201,0.5)`,
          background: 'transparent',
          color: MUTED,
          cursor: row.saving ? 'not-allowed' : 'pointer',
          flexShrink: 0,
          transition: 'color 0.15s, border-color 0.15s, transform 0.15s',
        }}
        onMouseEnter={(e) => {
          const btn = e.currentTarget as HTMLButtonElement;
          btn.style.color = palette.error['500'];
          btn.style.borderColor = 'rgba(224,82,82,0.4)';
        }}
        onMouseLeave={(e) => {
          const btn = e.currentTarget as HTMLButtonElement;
          btn.style.color = MUTED;
          btn.style.borderColor = 'rgba(189,188,201,0.5)';
        }}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      </button>
    </motion.div>
  );
}

// ─── Disabled State (no more milestones available) ────────────────────────────

// ─── Main Component ───────────────────────────────────────────────────────────

let draftCounter = 0;

/**
 * GamificationStep — Step 3 of the course creation wizard.
 *
 * Lets trainers enable/disable gamification and configure per-course milestones
 * with associated point values. All mutations are persisted immediately through
 * the API; the Save button simply advances the wizard.
 */
export default function GamificationStep({
  courseId,
  disabled = false,
  onSaved,
  entranceDelay,
  editMode = false,
}: GamificationStepProps) {
  const navigate = useNavigate();
  // Mirror the Step 1/4 pattern so StepCard auto-collapses on Save. When the
  // wizard opens in edit mode the step starts collapsed since gamification was
  // already configured; the user clicks Edit on the StepCard to make changes.
  const [saved, setSaved] = useState(editMode);

  // ── Tier gate ───────────────────────────────────────────────────────────
  const { data: subscription, isLoading: tierLoading } = useSubscription();
  const currentTier = subscription?.plan_id?.toLowerCase() ?? '';
  // Milestones are the only way points enter the ledger, so the step must
  // open wherever the leaderboard is promised (Growth+) — gating it on the
  // prize shop left Growth with a leaderboard nothing could ever fill
  // (Aleix, 2026-09-04). The prize shop stays Pro; it is configured
  // elsewhere and nothing in this step touches it.
  const isTierAllowed = planHasFeature(FEATURE.leaderboard, currentTier);
  const hasPrizeShop = planHasFeature(FEATURE.prizeShop, currentTier);

  // ── Server state ──────────────────────────────────────────────────────────
  const { data: config, isLoading: configLoading } = useGamificationConfig(courseId);
  const { data: milestones = [], isLoading: milestonesLoading } = useMilestones(courseId, {
    enabled: config?.is_enabled === true,
  });

  const enableGamification = useEnableGamification();
  const disableGamification = useDisableGamification();
  const createMilestone = useCreateMilestone();

  // ── Local draft rows (not yet committed) ─────────────────────────────────
  const [drafts, setDrafts] = useState<DraftRow[]>([]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const isEnabled = config?.is_enabled ?? false;
  const isLoading = configLoading || milestonesLoading;
  const isToggling = enableGamification.isPending || disableGamification.isPending;

  /** All milestone types currently occupied (persisted + drafts) */
  const usedTypes = new Set<MilestoneType>([
    ...milestones.map((m) => m.milestone_type),
    ...drafts.map((d) => d.milestone_type),
  ]);

  const allTypesTaken = usedTypes.size >= ALL_MILESTONE_TYPES.length;

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleToggle = useCallback(
    (next: boolean) => {
      if (next) {
        enableGamification.mutate(courseId);
      } else {
        disableGamification.mutate(courseId);
      }
    },
    [courseId, enableGamification, disableGamification]
  );

  const handleAddDraft = useCallback(() => {
    const availableTypes = ALL_MILESTONE_TYPES.filter((t) => !usedTypes.has(t));
    if (availableTypes.length === 0) return;
    draftCounter += 1;
    setDrafts((prev) => [
      ...prev,
      {
        localId: `draft-${draftCounter}`,
        milestone_type: availableTypes[0],
        points: '100',
        saving: false,
      },
    ]);
  }, [usedTypes]);

  const handleDraftChange = useCallback(
    (localId: string, patch: Partial<Pick<DraftRow, 'milestone_type' | 'points'>>) => {
      setDrafts((prev) =>
        prev.map((d) => (d.localId === localId ? { ...d, ...patch } : d))
      );
    },
    []
  );

  const handleDraftCommit = useCallback(
    (localId: string) => {
      const row = drafts.find((d) => d.localId === localId);
      if (!row) return;

      const parsed = parseInt(row.points, 10);
      if (isNaN(parsed) || parsed < 1) {
        // Discard invalid draft silently
        setDrafts((prev) => prev.filter((d) => d.localId !== localId));
        return;
      }

      // Mark as saving
      setDrafts((prev) =>
        prev.map((d) => (d.localId === localId ? { ...d, saving: true } : d))
      );

      createMilestone.mutate(
        {
          courseId,
          data: { milestone_type: row.milestone_type, points: parsed, is_enabled: true },
        },
        {
          onSuccess: () => {
            setDrafts((prev) => prev.filter((d) => d.localId !== localId));
          },
          onError: () => {
            setDrafts((prev) =>
              prev.map((d) => (d.localId === localId ? { ...d, saving: false } : d))
            );
          },
        }
      );
    },
    [courseId, createMilestone, drafts]
  );

  const handleDraftDiscard = useCallback((localId: string) => {
    setDrafts((prev) => prev.filter((d) => d.localId !== localId));
  }, []);

  const handleSave = useCallback(() => {
    // Commit any in-flight drafts before advancing
    drafts.forEach((d) => {
      if (!d.saving) handleDraftCommit(d.localId);
    });
    onSaved();
    setSaved(true);
  }, [drafts, handleDraftCommit, onSaved]);

  // ── Render ────────────────────────────────────────────────────────────────

  // onSave stays wired even when the tier locks the step: saving is then a
  // no-op acknowledgement that folds the card — parity with CertificateStep.
  return (
    <StepCard
      stepNumber={3}
      title="Gamification (optional)"
      disabled={disabled}
      onSave={handleSave}
      saved={saved}
      entranceDelay={entranceDelay}
    >
      {/* ── Tier-gated: show locked state if not Pro/Enterprise ────────── */}
      {!tierLoading && !isTierAllowed ? (
        <div
          style={{
            textAlign: 'center',
            padding: '16px 24px 24px',
            maxWidth: '480px',
            margin: '0 auto',
          }}
        >
          {/* Lock icon */}
          <div
            style={{
              width: '56px',
              height: '56px',
              borderRadius: '16px',
              background: 'rgb(var(--color-primary) / 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="3" y="11" width="18" height="11" rx="2" stroke={PURPLE} strokeWidth="2" />
              <path d="M7 11V7a5 5 0 0110 0v4" stroke={PURPLE} strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>

          <p
            style={{
              fontFamily: FONT,
              fontSize: '16px',
              fontWeight: 700,
              color: 'rgb(var(--color-text-primary))',
              margin: '0 0 8px',
              lineHeight: '24px',
            }}
          >
            Gamification is available on Pro & Enterprise
          </p>

          <p
            style={{
              fontFamily: FONT,
              fontSize: '13px',
              color: MUTED,
              margin: '0 0 20px',
              lineHeight: '20px',
            }}
          >
            Your current plan ({currentTier || 'Free'}) doesn't include gamification.
            From the Growth plan you get:
          </p>

          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: '0 0 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              textAlign: 'left',
              maxWidth: '340px',
              marginInline: 'auto',
            }}
          >
            {[
              'Point-based milestones to reward learner achievements',
              'A leaderboard with monthly seasons',
              'Prize catalog integration for real rewards (Pro)',
            ].map((text) => (
              <li
                key={text}
                style={{
                  fontFamily: FONT,
                  fontSize: '13px',
                  color: 'rgb(var(--color-text-primary))',
                  lineHeight: '20px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px',
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  style={{ flexShrink: 0, marginTop: '2px' }}
                >
                  <path d="M5 13l4 4L19 7" stroke={PURPLE} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {text}
              </li>
            ))}
          </ul>

          <button
            onClick={() => navigate('/settings?tab=billing')}
            className="active:scale-[0.97]"
            style={{
              fontFamily: FONT,
              fontSize: '14px',
              fontWeight: 700,
              color: '#fff',
              background: PURPLE,
              border: 'none',
              borderRadius: '24px',
              padding: '10px 28px',
              cursor: 'pointer',
              transition: 'background 0.15s, transform 0.15s',
            }}
            onMouseEnter={(e) => { (e.target as HTMLButtonElement).style.background = PURPLE_DARK; }}
            onMouseLeave={(e) => { (e.target as HTMLButtonElement).style.background = PURPLE; }}
          >
            Upgrade Plan
          </button>

          <p
            style={{
              fontFamily: FONT,
              fontSize: '11px',
              color: MUTED,
              margin: '12px 0 0',
              lineHeight: '16px',
            }}
          >
            You can skip this step — your course will work without gamification.
          </p>
        </div>
      ) : (
      <>
      {/* ── Toggle row ─────────────────────────────────────────────────── */}
      <div
        className="flex items-center gap-3"
        style={{ marginBottom: isEnabled ? '24px' : '0' }}
      >
        <Switch
          id="gamification-toggle"
          checked={isEnabled}
          onChange={handleToggle}
          disabled={isLoading || isToggling}
        />
        <label
          htmlFor="gamification-toggle"
          className="text-[15px] font-semibold text-text-primary select-none"
          style={{
            fontFamily: FONT,
            cursor: isLoading || isToggling ? 'default' : 'pointer',
          }}
        >
          {isEnabled ? 'Gamification enabled' : 'Gamification disabled'}
        </label>
        {!isEnabled && !isLoading && (
          <InfoTooltip
            ariaLabel="Why enable gamification"
            content={
              <div className="max-w-[260px]">
                <p className="text-sm font-semibold text-text-primary mb-1">
                  Why enable this?
                </p>
                <p className="text-xs text-text-secondary leading-relaxed">
                  Set milestones that motivate learners with points and
                  achievements. Points rank learners on the leaderboard
                  {hasPrizeShop
                    ? ' and can be redeemed for prizes in the reward catalog.'
                    : ' each season.'}
                </p>
              </div>
            }
          />
        )}
        {isToggling && (
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            aria-label="Saving…"
            style={{ animation: 'gam-spin 0.8s linear infinite', color: PURPLE }}
          >
            <circle cx="12" cy="12" r="9" stroke="rgb(var(--color-primary) / 0.25)" strokeWidth="3" />
            <path d="M12 3a9 9 0 019 9" stroke={PURPLE} strokeWidth="3" strokeLinecap="round" />
          </svg>
        )}
        {!isEnabled && (
          <span className="ml-auto text-xs text-text-tertiary">
            Enable to set milestones
          </span>
        )}
      </div>

      {/* ── Enabled state — milestone editor ─────────────────────────── */}
      <AnimatePresence mode="wait">
        {isEnabled && (
          <motion.div
            key="editor"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2, ease: EASE_OUT }}
          >
            {/* Subtitle */}
            <p
              style={{
                fontFamily: FONT,
                fontSize: '14px',
                color: MUTED,
                margin: '0 0 20px',
                lineHeight: '22px',
              }}
            >
              Choose a milestone(s) and their score weight
            </p>

            {/* Column headers */}
            {(milestones.length > 0 || drafts.length > 0) && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 120px 36px',
                  gap: '10px',
                  marginBottom: '10px',
                }}
              >
                <span
                  style={{
                    fontFamily: FONT,
                    fontSize: '11px',
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    color: MUTED,
                    textTransform: 'uppercase',
                  }}
                >
                  Milestone
                </span>
                <span
                  style={{
                    fontFamily: FONT,
                    fontSize: '11px',
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    color: MUTED,
                    textTransform: 'uppercase',
                  }}
                >
                  Score
                </span>
                <span />
              </div>
            )}

            {/* Loading skeleton */}
            {isLoading && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
                {[1, 2].map((i) => (
                  <div
                    key={i}
                    style={{
                      height: '40px',
                      borderRadius: '8px',
                      background: 'rgba(189,188,201,0.15)',
                      animation: 'gam-pulse 1.4s ease-in-out infinite',
                    }}
                  />
                ))}
              </div>
            )}

            {/* Persisted milestone rows */}
            {!isLoading && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '10px' }}>
                <AnimatePresence>
                  {milestones.map((m) => (
                    <motion.div
                      key={m.id}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.18, ease: EASE_OUT }}
                      style={{ overflow: 'hidden' }}
                    >
                      <PersistedMilestoneRow
                        milestone={m}
                        courseId={courseId}
                        onUpdate={() => {
                          /* optimistic update handled inside the row */
                        }}
                        onDelete={() => {
                          /* query invalidation handles re-render */
                        }}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}

            {/* Draft rows */}
            {drafts.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '10px' }}>
                <AnimatePresence>
                  {drafts.map((row) => (
                    <DraftMilestoneRow
                      key={row.localId}
                      row={row}
                      usedTypes={usedTypes}
                      onChange={handleDraftChange}
                      onCommit={handleDraftCommit}
                      onDiscard={handleDraftDiscard}
                    />
                  ))}
                </AnimatePresence>
              </div>
            )}

            {/* Empty state */}
            {!isLoading && milestones.length === 0 && drafts.length === 0 && (
              <p
                style={{
                  fontFamily: FONT,
                  fontSize: '13px',
                  color: MUTED,
                  textAlign: 'center',
                  margin: '8px 0 16px',
                  lineHeight: '22px',
                }}
              >
                No milestones yet. Add one below to get started.
              </p>
            )}

            {/* Add milestone button */}
            {!allTypesTaken && (
              <div style={{ textAlign: 'center', margin: '8px 0 20px' }}>
                <button
                  type="button"
                  onClick={handleAddDraft}
                  className="active:scale-[0.98]"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    border: `1.5px dashed ${PURPLE}`,
                    borderRadius: '8px',
                    background: 'transparent',
                    padding: '8px 20px',
                    fontFamily: FONT,
                    fontSize: '13px',
                    fontWeight: 600,
                    color: PURPLE,
                    cursor: 'pointer',
                    transition: 'background 0.15s, color 0.15s, transform 0.15s',
                  }}
                  onMouseEnter={(e) => {
                    const btn = e.currentTarget as HTMLButtonElement;
                    btn.style.background = 'rgb(var(--color-primary) / 0.06)';
                  }}
                  onMouseLeave={(e) => {
                    const btn = e.currentTarget as HTMLButtonElement;
                    btn.style.background = 'transparent';
                  }}
                >
                  <PlusIcon />
                  Add Milestone
                </button>
              </div>
            )}

            {/* All types taken notice */}
            {allTypesTaken && (
              <p
                style={{
                  fontFamily: FONT,
                  fontSize: '12px',
                  color: MUTED,
                  textAlign: 'center',
                  margin: '4px 0 20px',
                }}
              >
                All milestone types have been added.
              </p>
            )}

            {/* Reminder banner */}
            <ReminderBanner
              storageKey={REMINDER_STORAGE_KEYS[3]}
              title={REMINDER_MESSAGES[3].title}
              message={REMINDER_MESSAGES[3].message}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Keyframe animations */}
      <style>{`
        @keyframes gam-spin {
          to { transform: rotate(360deg); }
        }
        @keyframes gam-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.45; }
        }
      `}</style>
      </>
      )}
    </StepCard>
  );
}
